import { UrlMatchResult, UrlSegment } from '@angular/router';

/**
 * Registration-series pages live at /plates/{NN}-reg (/plates/26-reg,
 * /plates/76-reg). This is the permanent canonical form agreed for the SEO
 * architecture; /new-release/{NN} is a legacy alias that 301s here.
 *
 * Series identifiers: 02-26 (and onwards) are the March releases of 20NN;
 * 51-76 (and onwards) are the September releases of 20(NN-50).
 */

const SERIES_SLUG = /^(\d{2})-reg$/;

/**
 * Series with a published page (curated data in assets/data/new-release).
 * A new series goes live by adding its data file and its code here; the CDN
 * edge function carries the same list (deploy/edge) so unpublished series 404.
 */
export const PUBLISHED_SERIES: string[] = [
  '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14',
  '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27',
  '51', '52', '53', '54', '55', '56', '57', '58', '59', '60', '61', '62', '63',
  '64', '65', '66', '67', '68', '69', '70', '71', '72', '73', '74', '75', '76',
];

export function isSeriesCode(nn: string): boolean {
  if (!/^\d{2}$/.test(nn)) return false;
  const n = parseInt(nn, 10);
  return (n >= 2 && n <= 49) || (n >= 51 && n <= 99);
}

export function seriesPath(nn: string | number): string {
  return `/plates/${String(nn).padStart(2, '0')}-reg`;
}

/** Router matcher for /plates/{NN}-reg; exposes NN as the releaseYear param. */
export function seriesMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 2 || segments[0].path !== 'plates') return null;
  const m = SERIES_SLUG.exec(segments[1].path);
  if (!m || !PUBLISHED_SERIES.includes(m[1])) return null;
  return {
    consumed: segments,
    posParams: { releaseYear: new UrlSegment(m[1], {}) },
  };
}

const MONTHS = { march: 'March', september: 'September' };

/** "March 2026" for 26, "September 2026" for 76. */
export function seriesPeriodLabel(nn: string): string {
  const n = parseInt(nn, 10);
  return n >= 51
    ? `${MONTHS.september} ${2000 + n - 50}`
    : `${MONTHS.march} ${2000 + n}`;
}

/** Page metadata shared by the router (route-data-service) and the static head build. */
export function seriesMeta(nn: string): { title: string; description: string; heading: string } {
  const period = seriesPeriodLabel(nn);
  return {
    heading: `${nn} Number Plates`,
    title: `${nn} Number Plates | ${nn} Reg Plates for Sale | PlateX`,
    description:
      `${nn} reg plates carry the ${nn} age identifier from the DVLA's ${period} registration series. ` +
      `Compare ${nn} plates across UK sellers and see them on your car.`,
  };
}

/** Chronological order: 02 < 51 < 03 < 52 ... (March then September of each year). */
export function seriesSortKey(nn: string): number {
  const n = parseInt(nn, 10);
  return n >= 51 ? (n - 50) * 2 + 1 : n * 2;
}
